/* libs */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { isEmpty, isEqual } from "lodash";

/* components */
import { Text, Select, Option } from "informed";
import IntegrationModuleFields from "./Integration";
import ModuleFields from "./ModuleFields/";
import formWrapper from "../../../shared/Form";
import * as localStorageHelpers from "../../../../helpers/localStorage";
import { TextInput, SelectInput, PublishAtPicker } from "../../../../components/shared/Forms";
import * as controlPanelActions from "../../../../actions/controlPanelActions";
import * as integrationsActions from "../../../../actions/integrationActions";
import * as globalActions from "../../../../actions/globalActions";
import * as oauthActions from "../../../../actions/oauthActions";
import * as stringHelpers from "../../../../helpers/strings";
import EverythingModule from "./EverythingModule";

// Modules that only global users can see in the picker or configure on an existing page
const GLOBAL_ONLY_MODULE_KEYS = ["@hillsong-technology/verris-module-autoreg"];

class Form extends Component {
	constructor(props) {
		super(props);
		const {
			user: { user },
			values,
		} = props;
		let modules = this.buildModuleList(props.modules);

		this.state = {
			moduleId: values ? values.moduleId : null,
			modules,
			selectedModule: null,
			isIntegrationModule: false,
			hasBeenRedirected: false,
		};
	}

	/* user + module helpers */

	// Global users (owner/master global role) can see and configure every module
	isGlobalUser = () => {
		const {
			user: { user },
		} = this.props;
		return !!user && ["owner", "master"].includes(user.globalRole);
	};

	// Adds the Everything module to the picker for master users
	addEverythingModule = modules => {
		let newModules = modules.concat();
		const {
			user: { user },
		} = this.props;
		if (user) {
			const { globalRole } = user;
			if (globalRole && globalRole.toLowerCase() === "master") {
				newModules.push({
					type: "page",
					id: "everything",
					key: "everything",
					name: "Everything Module (Global Users Only)",
					properties: EverythingModule,
				});
			}
		}
		return newModules;
	};

	// Removes global-only modules from the picker for everyone else
	removeGlobalOnlyModules = modules => {
		if (this.isGlobalUser()) {
			return modules;
		}
		return modules.filter(module => !GLOBAL_ONLY_MODULE_KEYS.includes(module.key));
	};

	// The full picker list for the current user
	buildModuleList = modules => this.addEverythingModule(this.removeGlobalOnlyModules(modules));

	// The module backing the page being edited, or null for a new page
	currentModule = () => {
		if (this.props.values.moduleId) {
			return this.props.modules.find(module => {
				return module.id === this.props.values.moduleId;
			});
		}
		return null;
	};

	// True when the page's module is hidden from the current user, so its fields must not render
	isRestrictedModule = () => {
		const module = this.currentModule();
		return !!module && GLOBAL_ONLY_MODULE_KEYS.includes(module.key) && !this.isGlobalUser();
	};

	/* lifecycle */

	componentDidMount() {
		if (!this.props.hasFormApi) {
			this.props.getFormApi(this.props.formApi);
		}

		const { initialValues, modules } = this.props;
		if (initialValues.moduleType === "OAuthIntegration") {
			const module = modules.find(m => m.id === initialValues.moduleId);
			this.setState({
				isIntegrationModule: true,
				hasBeenRedirected: true,
				selectedModule: module,
			});
		}

		this.setOrCreateFirebaseDoc();
	}
	componentDidUpdate(prevProps) {
		if (!this.props.hasFormApi) {
			this.props.getFormApi(this.props.formApi);
		}

		const { formApi, oauth } = this.props;
		const { isIntegrationModule, hasBeenRedirected } = this.state;

		const integrationDataLocalStorage = localStorageHelpers.getObjectFromStorage("verris:integration");

		// if integrationDataLocalStorage object is not empty
		//	- if we have redirect values from localStorage
		//	  - save auth code to api
		//	  - apply old form state
		//    - remove localStorage data
		//    - update state
		if (!isEmpty(integrationDataLocalStorage)) {
			const redirectValues = localStorageHelpers.getObjectFromStorage("verris:redirectValues");
			if (redirectValues) {
				const { provider, formState, module } = localStorageHelpers.getObjectFromStorage("verris:integration");

				this.saveAuthCode(provider, redirectValues.code, module);

				localStorageHelpers.removePropertyFromObject("verris:integration", "formState");
				formState.values.provider = provider;
				formApi.setState(formState);

				localStorageHelpers.removePropertyFromObject("verris:integration", "isIntegrationModule");
				localStorageHelpers.removeFromStorage("verris:redirectValues");

				this.setState({
					isIntegrationModule: true,
					hasBeenRedirected: true,
					selectedModule: module,
				});
			} else {
				// restart the whole thing
				localStorageHelpers.removeFromStorage("verris:integration");
			}
		}

		if (!isEmpty(oauth.integrationData) && isIntegrationModule && hasBeenRedirected) {
			// we need to wait for redux to have our integration to update the form values
			const existingFormValues = formApi.getState().values;
			if (existingFormValues && (!existingFormValues.accessToken || !existingFormValues.refreshToken)) {
				if (oauth && oauth.integrationData) {
					delete existingFormValues.themeId;
					formApi.setValues({
						...existingFormValues,
						accessToken: oauth.integrationData.response.access_token,
						refreshToken: oauth.integrationData.response.refresh_token,
						refreshedAt: oauth.integrationData.refreshedAt,
					});
				}
			}
		}

		if (!isEqual(prevProps.modules, this.props.modules) || !isEqual(prevProps.user, this.props.user)) {
			this.setState({ modules: this.buildModuleList(this.props.modules) });
		}

		if (!isEqual(prevProps.values, this.props.values)) {
			const errors = formApi.getState().errors;
			if (Object.keys(errors).length > 0) {
				this.props.setAppetizePause(true);
			} else {
				this.setOrCreateFirebaseDoc();
			}
		}
	}

	setOrCreateFirebaseDoc = () => {
		var id = this.props.values.id
			? this.props.values.id + "-" + this.props.auth.user.uid
			: "new-" + this.props.auth.user.uid;
		let module = this.currentModule();
		let data = Object.assign({}, this.props.values);
		if (module) {
			data["moduleKey"] = module.key;
		}
		this.props.actions.upsertFirebaseObject("page", id, data);
		if (data.themeId && data.themeId !== "" && data.name && data.name !== "" && data.title && data.title !== "") {
			this.props.setAppetizePageId(id, this.props.values.moduleId);
		}
	};

	buildSelector(field, values) {
		return (
			<Select field={field} id={field}>
				<Option value="" disabled>
					Select One...
				</Option>
				{values.map(value => (
					<Option key={value.id} value={value.id}>
						{value.name}
					</Option>
				))}
			</Select>
		);
	}

	changeModule = nextModule => {
		const { formApi } = this.props;
		formApi.setValue("moduleId", nextModule.id);
		this.setState({ selectedModule: nextModule });
	};

	buildModules = modules => {
		let moduleId = this.props.values ? this.props.values.moduleId : null;
		return (
			<div className="modules-list">
				{modules.map(module => {
					const moduleType = this.props.moduleType || "page";
					if (
						(module.type === moduleType || (moduleType === "identity" && module.type === "integration")) &&
						!(
							[
								"@hillsong-technology/verris-module-brushfire-authentication",
								"@hillsong-technology/verris-module-chat",
							].includes(module.key) && !this.isGlobalUser()
						)
					) {
						const isIntegrationModule = module.type === "integration";
						return (
							<div
								className={`module-item ${moduleId ? (moduleId !== module.id ? "off" : "on") : ""}`}
								key={module.key}
								onClick={() => {
									this.changeModule(module);
									isIntegrationModule && this.setIntegration();
								}}
							>
								<div className="module-icon">{module.name}</div>
							</div>
						);
					} else {
						return null;
					}
				})}
			</div>
		);
	};

	setIntegration = () => {
		const possibleExistingData = localStorageHelpers.getObjectFromStorage("verris:integration");
		const initIntegration = { isIntegrationModule: true };
		if (possibleExistingData) {
			localStorageHelpers.replaceObjectInStorage("verris:integration", initIntegration);
		} else {
			localStorageHelpers.addDataToObject("verris:integration", initIntegration);
		}
		this.setState({ isIntegrationModule: true });
	};

	connectIntegration = () => {
		const module = this.props.modules.find(module => module.id === this.props.values.moduleId);
		const { authorizeUrl, authorizeUrlLocal } = module.integrationData;
		const url = process.env.NODE_ENV === "development" ? authorizeUrlLocal : authorizeUrl;

		// save to localStorage
		// - provider in localStorage
		// - current location in localStorage
		// - current form state in localStorage
		localStorage.setItem("verris:redirectTo", window.location.href);
		localStorageHelpers.addDataToObject("verris:integration", {
			provider: module.integrationData.provider,
			formState: this.props.formApi.getState(),
			module: this.state.selectedModule,
		});

		window.location.href = url;
	};

	saveAuthCode = (provider, authCode, module) => {
		const { oauthActions } = this.props;
		const isDev = process.env.NODE_ENV === "development";
		// TODO need to remove the Environment property completely
		const data = {
			provider,
			authCode,
			contentType: module.integrationData.contentType,
			useAuth: module.integrationData.useAuth,
			tokenUrl: module.integrationData.tokenUrl,
			environment: isDev ? "local" : "production",
			properties: isDev ? module.integrationData.tokenRequestLocal : module.integrationData.tokenRequest,
		};
		oauthActions.saveAuthCode(data);
	};

	refreshAccessToken = () => {
		const { oauthActions, values } = this.props;
		const {
			selectedModule: { integrationData },
		} = this.state;
		const isDev = process.env.NODE_ENV === "development";

		const data = {
			integrationId: values.id,
			environment: isDev ? "local" : "production",
			properties: isDev ? integrationData.refreshRequestLocal : integrationData.refreshRequest,
		};

		oauthActions.refreshAccessToken(data);
	};

	startLoader = () => {
		this.props.controlPanelActions.showLoading();
		this.props.formApi.submitForm();
	};

	preventEnterSubmit = e => {
		const { isIntegrationModule } = this.state;
		if (isIntegrationModule && e.key === "Enter") {
			e.preventDefault();
		}
	};

	render() {
		const { actionType, experiences, initialValues, isCopy, oauth, themes, values } = this.props;
		const { hasBeenRedirected, isIntegrationModule, modules, selectedModule } = this.state;
		let themeData = [];
		themes.forEach(theme => {
			themeData.push({ key: theme.id, value: theme.id, label: theme.name });
		});
		let experienceData = [];
		if (isCopy && experiences) {
			experiences.experiences.forEach(experience => {
				experienceData.push({ key: experience.id, value: experience.id, label: experience.name });
			});
		}

		// flags to make things less messy
		const secondOAuthIntegrationStep = isIntegrationModule && hasBeenRedirected;
		const showModuleFields =
			(secondOAuthIntegrationStep || !isIntegrationModule) && modules && values && values.moduleId;

		const errorWithNewIntegration =
			secondOAuthIntegrationStep && (oauth && oauth.errorMessage) && actionType !== "update";
		const restrictedModule = this.isRestrictedModule();

		return (
			<div>
				{modules ? this.buildModules(modules) : "Loading..."}
				<Text field="moduleId" id="moduleId" type="hidden" />
				{isCopy && (
					<SelectInput
						field="experienceId"
						id="experienceId"
						values={experienceData}
						placeholder="Move Page To Experience"
						isRequired={true}
					/>
				)}
				{values && values.moduleId && restrictedModule && (
					<div className="page-builder-content">
						<p>You are not allowed to configure this module.</p>
					</div>
				)}
				{values && values.moduleId && !restrictedModule && (
					<div className="page-builder-content">
						<TextInput
							field="name"
							id="name"
							placeholder="Page Name"
							autocomplete="off"
							isRequired={true}
							onKeyPress={this.preventEnterSubmit}
						/>
						{isIntegrationModule ? (
							<IntegrationModuleFields
								actionType={actionType}
								connectIntegration={this.connectIntegration}
								hasBeenRedirected={hasBeenRedirected}
								lastRefreshedAt={values.refreshedAt}
								oauth={oauth}
								providerName={selectedModule.integrationData.provider}
								refreshClick={this.refreshAccessToken}
							/>
						) : (
							<React.Fragment>
								{(!this.props.moduleType || this.props.moduleType === "page") && (
									<TextInput
										field="title"
										id="title"
										placeholder="Page Title"
										autocomplete="off"
										isRequired={true}
									/>
								)}
								{themeData.length > 0 && (
									<SelectInput
										field="themeId"
										id="themeId"
										values={themeData}
										placeholder="Theme"
										isRequired={true}
									/>
								)}

								<PublishAtPicker
									field="publishAt"
									id="publishAt"
									placeholder="Date and Time to Publish Page"
								/>
							</React.Fragment>
						)}

						{showModuleFields && (
							<div>
								<ModuleFields
									values={values}
									initialValues={initialValues}
									modules={modules}
									moduleId={values.moduleId}
									formApi={this.props.formApi}
									appKey={this.props.appKey}
								/>
							</div>
						)}

						{((secondOAuthIntegrationStep && !errorWithNewIntegration) || !isIntegrationModule) && (
							<div className="btn btn-success" onClick={this.startLoader}>
								{this.props.buttonLabel}
							</div>
						)}
					</div>
				)}
			</div>
		);
	}
}

Form.propTypes = {
	actions: PropTypes.object,
	actionType: PropTypes.string,
	auth: PropTypes.object,
	appKey: PropTypes.string,
	buttonLabel: PropTypes.string,
	controlPanelActions: PropTypes.object,
	experiences: PropTypes.object,
	formApi: PropTypes.object,
	getFormApi: PropTypes.func,
	hasFormApi: PropTypes.bool,
	initialValues: PropTypes.object,
	integrations: PropTypes.array,
	isCopy: PropTypes.bool,
	modules: PropTypes.array,
	moduleType: PropTypes.string,
	oauth: PropTypes.object,
	oauthActions: PropTypes.object,
	setAppetizePageId: PropTypes.func,
	setAppetizePause: PropTypes.func,
	themes: PropTypes.array,
	user: PropTypes.object,
	values: PropTypes.object,
};
const mapStateToProps = state => ({
	apps: state.apps,
	auth: state.auth,
	experiences: state.experiences,
	oauth: state.oauth,
	pages: state.pages,
	user: state.user,
});

const mapDispatchToProps = dispatch => ({
	actions: bindActionCreators(globalActions, dispatch),
	controlPanelActions: bindActionCreators(controlPanelActions, dispatch),
	integrationsActions: bindActionCreators(integrationsActions, dispatch),
	oauthActions: bindActionCreators(oauthActions, dispatch),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(formWrapper(Form));
